import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ChevronRight } from 'lucide-react';
import { getPlans } from '../services/authApi';

const PLAN_COPY = {
  Basic: {
    desc: 'For beginners who want a simple, streamlined learning experience.',
    isPopular: false,
  },
  Standard: {
    desc: 'For intermediate marketers managing multiple channels who want more tools.',
    isPopular: false,
  },
  Premium: {
    desc: 'All courses & tools, with additional features designed for growing agencies.',
    isPopular: true,
  },
};

const fallbackPlans = [
  {
    id: 'fallback-basic',
    name: 'Basic',
    description: 'Access to Basic Courses\nMobile-friendly access on any device\nCancel Anytime',
    price: 29,
  },
  {
    id: 'fallback-standard',
    name: 'Standard',
    description: 'Access to Advanced Courses\nSupports access on multiple devices\nLimited Downloads\nLimited Templates\nCancel Anytime',
    price: 59,
  },
  {
    id: 'fallback-premium',
    name: 'Premium',
    description: 'Access to All Courses\nUnlimited simultaneous devices\nUnlimited Downloads\nUnlimited Templates & Calendars\nIncludes priority support\nCancel Anytime',
    price: 99,
  },
];

function formatPrice(price) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(Number(price || 0));
}

function normalizePlan(plan) {
  const copy = PLAN_COPY[plan.name] || {};

  return {
    ...plan,
    desc: copy.desc || plan.description || 'Choose this plan to continue your learning.',
    priceLabel: formatPrice(plan.price),
    period: 'Per month',
    features: String(plan.description || '')
      .split('\n')
      .map((feature) => feature.trim())
      .filter(Boolean),
    isPopular: Boolean(copy.isPopular),
  };
}

function orderPlansForDisplay(plans) {
  const order = {
    Basic: 0,
    Premium: 1,
    Standard: 2,
  };

  return [...plans].sort((first, second) => {
    const firstOrder = order[first.name] ?? 99;
    const secondOrder = order[second.name] ?? 99;
    return firstOrder - secondOrder;
  });
}

export default function Pricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(fallbackPlans.map(normalizePlan));
  const [plansError, setPlansError] = useState('');

  useEffect(() => {
    if (!localStorage.getItem('authToken')) {
      localStorage.removeItem('paymentSubmitted');
      return;
    }

    if (localStorage.getItem('paymentSubmitted') === 'true') {
      navigate('/my-account', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const data = await getPlans();
        setPlans(orderPlansForDisplay(data.map(normalizePlan)));
        setPlansError('');
      } catch (error) {
        setPlansError(error.message);
      }
    };

    loadPlans();
  }, []);

  if (localStorage.getItem('authToken') && localStorage.getItem('paymentSubmitted') === 'true') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0F172A] text-white">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-slate-700 border-t-[#2563EB]" />
          <p className="text-sm font-semibold text-slate-400">Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen font-sans text-white bg-[#0F172A]  relative">

      {/* GLOBAL BACKGROUND GLOWS */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&q=80&w=2400"
          alt="Background"
          className="w-full h-full object-cover opacity-[0.03] mix-blend-screen"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/80 via-[#0F172A]/95 to-[#0F172A]" />

        {/* Glows matching the new color scheme */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#2563EB]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#F43F5E]/10 rounded-full blur-[150px]" />
      </div>

      {/* HEADER */}
      <div className="relative z-50">
        <Header />
      </div>

      {/* MAIN CONTENT AREA */}
      <main className="flex-1 flex flex-col items-center w-full max-w-7xl mx-auto px-4 relative z-10 pt-40 pb-32">

        {/* Title Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mb-6">
            Simple, transparent <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">pricing</span>
          </h1>
          <p className="text-slate-400 text-lg md:text-xl leading-relaxed">
            Choose the plan that best fits your marketing goals. Upgrade, downgrade, or cancel anytime.
          </p>
          {plansError && <p className="text-[#F43F5E] text-sm mt-4">{plansError}</p>}
        </div>

        {/* Pricing Unified Table Grid (Matches Reference Layout) */}
        <div className="w-full max-w-6xl mx-auto bg-[#1E293B] border border-slate-700/80 rounded-[2rem] shadow-2xl overflow-hidden flex flex-col md:flex-row">

          {orderPlansForDisplay(plans).map((plan, index) => (
            <div
              key={plan.id || plan.name}
              className={`flex flex-col flex-1 border-b md:border-b-0 border-slate-700/80 
              ${index !== plans.length - 1 ? 'md:border-r' : ''}`}
            >

              {/* Top Half: Pricing & Button */}
              <div className="flex flex-col p-8 lg:p-10 bg-[#1E293B]">

                {/* Title & Badge Row */}
                <div className="flex items-start justify-between mb-3 h-8">
                  <h3 className="text-2xl font-bold text-white tracking-tight">
                    {plan.name}
                  </h3>
                  {plan.isPopular && (
                    <span className="border bg-clip-border border-[#2563EB] text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md shadow-sm text-transparent bg-clip-text bg-gradient-to-r from-[#2563EB] to-[#F43F5E]">
                      Best Value
                    </span>
                  )}
                </div>

                {/* Fixed height description to keep pricing rows aligned perfectly */}
                <div className="h-[80px] mb-4">
                  <p className="text-sm text-slate-400 leading-relaxed pr-4">
                    {plan.desc}
                  </p>
                </div>

                {/* Price & Period */}
                <div className="mt-auto">
                  <div className="text-4xl lg:text-5xl font-black text-white mb-1">
                    {plan.priceLabel}
                  </div>
                  <div className="text-xs font-medium text-slate-500 mb-8">
                    {plan.period}
                  </div>

                  {/* Constant Style Button */}
                  <Link
                    to="/checkout"
                    state={{ planId: plan.id }}
                    className="w-full py-3.5 px-6 rounded-xl font-bold text-sm bg-[#2563EB] text-white hover:bg-[#1d4ed8] hover:shadow-[0_0_15px_rgba(37,99,235,0.4)] transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    Get started <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Bottom Half: Features List */}
              <div className="flex-1 p-8 lg:p-10 bg-[#151E2E] border-t border-slate-700/80">
                <ul className="space-y-5">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      {/* Tiny dot to precisely match the clean look of the reference image */}
                      <div className="w-1.5 h-1.5 rounded-full bg-[#F43F5E] mt-1.5 shrink-0 shadow-[0_0_5px_rgba(244,63,94,0.5)]" />
                      <span className="text-sm text-slate-300 leading-relaxed">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

            </div>
          ))}
        </div>

      </main>

      <div className="relative mt-auto">
        <Footer />
      </div>
    </div>
  );
}
